/**
 * What the Meddyg actually does (M4-08).
 *
 * One villager's services, and nothing else: the schedule, the conversation and
 * the intent handler are `village.ts`'s, because those belong to *everybody* who
 * lives in Llan-y-Nant. What is left here is the thing only she can do — clean a
 * wound properly, and tell you the truth about a leg — which is the right size
 * for a file named after a person.
 *
 * She is deliberately not free healing. She is slow, she wants paying in what
 * her shelves need, she never leaves the village, and she can die: she is an
 * actor with a body like everybody else, and a player who kills her has taken
 * the valley's healer away for good. That consequence is the game, not a bug.
 *
 * What she will *not* do is beat a practised player with honey and good light.
 * The ceiling belongs to players — M4-08 is explicit — and that ceiling is in
 * the dice below rather than in a special case.
 */
import { infectionStageOf } from './infection.js';
import { createLore, growMedicine, removeItem } from './item.js';
import { rollTreatment } from './medicine.js';
import { TICKS_PER_SECOND } from './time.js';
import { line, say } from './voice.js';
/**
 * What she takes in payment: anything her shelves want.
 *
 * A tag query rather than a price list, and that is the difference between her
 * and the smith: she takes *anything that restocks her*, and one yarrow is as
 * good as another, where M5-01's barter has to be able to say that a boar tusk
 * is worth twenty rabbits. Both are honest and neither would do the other's job.
 */
export const PAYMENT_TAGS = ['herb', 'meat', 'dressing'];
/** Dice she brings to a treatment, over and above the wit any villager has. */
export const MEDDYG_BONUS = 2;
/** How much blood a success of hers presses out of a bleed. As anybody's. */
const BLEED_PER_SUCCESS = 3;
/** What she takes off a wound's infection risk when she washes it properly. */
export const MEDDYG_CLEAN_FACTOR = 0.5;
/** How long she takes over a treatment, for a client to draw a wait on. */
export const TREAT_TICKS = TICKS_PER_SECOND * 20;
/**
 * Treat whoever asked, if they have paid and there is anything to treat.
 *
 * She does the whole of the ordinary ladder in one go — wash, dress, bind —
 * because a player who has walked to the village and handed over a bag of
 * yarrow should not then have to ask four times. What she cannot do is beat a
 * practised player with honey and good light, and that ceiling is in the dice:
 * her pool is `MEDDYG_BONUS` over a plain wit roll, which lands between a
 * novice's and a maxed player's (asserted in `villagers.test.ts`).
 */
export function treat(ctx, actor, patient, healer, def) {
    const wound = worstOf(patient.vitals.wounds);
    if (!wound) {
        say(ctx, healer, actor, line(def.lines.nothingWrong, ctx.tick), 'nothing-wrong');
        return;
    }
    const paid = takePayment(ctx, actor);
    if (!paid) {
        say(ctx, healer, actor, line(def.lines.unpaid, ctx.tick), 'unpaid');
        return;
    }
    const roll = rollTreatment(ctx.rng('medicine'), Math.max(1, MEDDYG_BONUS + (def.medicine ?? 0) / 15));
    wound.bleedRate = Math.max(0, wound.bleedRate - roll.successes * BLEED_PER_SUCCESS);
    wound.dirty = false;
    wound.infectionMod *= MEDDYG_CLEAN_FACTOR;
    wound.dressedAt = ctx.tick;
    if (wound.state === 'fresh')
        wound.state = 'treated';
    // Watching her work is how the valley learns medicine (M4-01's mentoring
    // rule, with its permanent anchor). She is *always* more practised than the
    // patient, so this is the one place growth is unconditional.
    const lore = (ctx.world.lore[actor.id] ??= createLore());
    growMedicine(lore, true);
    say(ctx, healer, actor, line(def.lines.treated, ctx.tick), 'treated');
    // …and the truthful diagnosis, when there is one to give. This is the line
    // M4-05's amputation conversation actually starts from.
    const rotten = patient.vitals.wounds.find((one) => infectionStageOf(one) === 'gangrenous' || infectionStageOf(one) === 'septic');
    if (rotten)
        say(ctx, healer, actor, line(def.lines.gangrene, ctx.tick), 'gangrene');
}
/** The wound she works on: the one most likely to kill them. */
function worstOf(wounds) {
    let worst = null;
    for (const wound of wounds) {
        if (wound.state === 'healed')
            continue;
        const score = wound.bleedRate * 10 + wound.infectionMod * 5 + wound.level;
        const best = worst ? worst.bleedRate * 10 + worst.infectionMod * 5 + worst.level : -1;
        if (score > best)
            worst = wound;
    }
    return worst;
}
/** Take one thing off the asker's hands, for her shelves. `false` if they have nothing. */
function takePayment(ctx, actor) {
    const inventory = ctx.world.inventories[actor.id];
    if (!inventory)
        return false;
    for (const stack of inventory.items) {
        if (stack.count <= 0)
            continue;
        const def = ctx.catalogue.item?.(stack.defId);
        if (!def?.tags.some((tag) => PAYMENT_TAGS.includes(tag)))
            continue;
        removeItem(inventory, stack.defId, 1);
        return true;
    }
    return false;
}
//# sourceMappingURL=meddyg.js.map